// @ts-strict-ignore
import * as React from 'react';
import { RootState } from '../../reducers/reducers';
import { useSelector } from 'react-redux';
import Text from 'antd/lib/typography/Text';
import {
    selectAppPlatform,
    selectAppSettingAutomations,
    selectAppTimeFormatSetting,
    selectAuthCredentials,
    selectEffectiveTimezone,
    selectIsDemoInstance,
    selectRelease,
} from '../../selectors/selectors';
import { CheckCircleFilled, ClockCircleOutlined, RetweetOutlined } from '@ant-design/icons';
import { allLifecycleEventsForPlatform } from '../automations/automationHelperComponents';
import { antGrey, runwayColors } from '../styles/globalStyles';
import {
    AutomationPromoteStablePhasedRelease,
    AutomationType,
    CompletedReleaseSummary,
    LifecycleEventType,
    LifecycleStep,
    NextReleaseSummary,
    ReleaseCalendarEventType,
    ReleaseCalendarLifecycleEvent,
    UpcomingReleaseSummary,
} from '../../types';
import Divider from 'antd/lib/divider';
import { FeatureSection } from '../../typescriptHelpers';
import { EmptyStateWrapper } from '../releaseEmptyState/EmptyStateWrapper';
import { useReleaseLifecycleStepDisplayInfo } from '@/utils/hooks/helpers';
import TimeWithConditionalTz from '@/components/common/TimeWithConditionalTz';
import dayjs from 'dayjs';
import * as API from '../../api';
import { DatetimeFormat, formatDateWithTimezone } from '@/timeHelpers';

interface TargetDatesSectionProps {
    orgId: string;
    appId: string;
    releaseId: string;
}

const TargetDatesSection: React.FC<TargetDatesSectionProps> = (props: TargetDatesSectionProps) => {
    const { orgId, appId, releaseId } = props;

    const appPlatform = useSelector((state: RootState) => selectAppPlatform(state, appId));
    const release = useSelector((state: RootState) => selectRelease(state, releaseId));
    const platformLifecycleSteps = allLifecycleEventsForPlatform(appPlatform);
    const lifecycleSteps =
        release?.targetReleaseDate && !platformLifecycleSteps.includes(LifecycleStep.Release) ?
            [...platformLifecycleSteps, LifecycleStep.Release]
        :   platformLifecycleSteps;

    const automations = useSelector((state: RootState) => selectAppSettingAutomations(state, appId));
    const autoPromoteAutomation = automations.find((a) => a.type === AutomationType.AutoPromoteStablePhasedReleases) as
        | AutomationPromoteStablePhasedRelease
        | undefined;
    const showFullRolloutBlock = !!autoPromoteAutomation?.enabled;

    return (
        <EmptyStateWrapper
            appId={appId}
            featureSections={[FeatureSection.OverviewTopOverview]}
            releaseId={releaseId}
            customWrapperStyle={{
                minHeight: 'unset',
                padding: '12px 24px',
                margin: '24px 0',
                marginRight: 'auto',
            }}
        >
            {(isInEmptyState) => (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        padding: isInEmptyState ? 20 : '10px 0px 24px 0px',
                    }}
                >
                    {lifecycleSteps.map((step, index) => {
                        return (
                            <div key={index} style={{ display: 'flex', minHeight: '100px' }}>
                                {index ?
                                    <Divider type="vertical" style={{ height: '100%', margin: '0px 20px' }} />
                                :   ''}
                                <DateBlock step={step} releaseId={releaseId} appId={appId} />
                            </div>
                        );
                    })}
                    {showFullRolloutBlock && <FullRolloutDateBlock orgId={orgId} appId={appId} releaseId={releaseId} />}
                </div>
            )}
        </EmptyStateWrapper>
    );
};

const FullRolloutDateBlock: React.FC<{ orgId: string; appId: string; releaseId: string }> = ({
    orgId,
    appId,
    releaseId,
}) => {
    const authCreds = useSelector((state: RootState) => selectAuthCredentials(state));
    const isDemoInstance = useSelector((state: RootState) => selectIsDemoInstance(state, orgId));
    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, appId));
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, appId));

    const [rolloutEvent, setRolloutEvent] = React.useState<ReleaseCalendarLifecycleEvent | null>(null);

    React.useEffect(() => {
        if (isDemoInstance || !authCreds) {
            return () => {};
        }
        let cancelled = false;
        API.getReleaseCalendar({ orgId, appId, releaseId }, authCreds)
            .then((response) => {
                if (cancelled) return;
                const releaseData = Object.values(response.releaseCalendar ?? {})[0];
                const event = releaseData?.stepLifecycleEvents?.find(
                    (e) => e.lifecycleEventType === LifecycleEventType.LifecycleEventTypeRolloutCompletion,
                );
                setRolloutEvent(event ?? null);
            })
            .catch(() => {
                if (cancelled) return;
                setRolloutEvent(null);
            });
        return () => {
            cancelled = true;
        };
    }, [orgId, appId, releaseId, authCreds, isDemoInstance]);

    const isComplete = rolloutEvent?.dateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeActualDate;
    const isPredicted = rolloutEvent?.dateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeEstimatedDate;
    const eventDate = rolloutEvent?.startDate ? dayjs(rolloutEvent.startDate) : null;
    const hasDate = !!eventDate?.isValid();

    if (!rolloutEvent || !hasDate) {
        return null;
    }

    const headerText = isComplete ? 'Full Rollout' : 'Target Full Rollout Date';
    const displayTime = formatDateWithTimezone(
        eventDate,
        'shortHourOnlyWithoutTimezone',
        effectiveTimezone,
        use24HourTimeFormat,
    );
    const displayDate = eventDate.tz(effectiveTimezone).format(DatetimeFormat.DayMonthDate);

    return (
        <div style={{ display: 'flex', minHeight: '100px' }}>
            <Divider type="vertical" style={{ height: '100%', margin: '0px 20px' }} />
            <div style={{ minWidth: '180px' }}>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        paddingBottom: '6px',
                        gap: '10px',
                    }}
                >
                    {isPredicted && <ClockCircleOutlined style={{ fontSize: 14, color: antGrey.grey6 }} />}
                    <Text style={{ fontSize: '14px', color: antGrey.grey6 }}>{headerText}</Text>
                    {isComplete && <CheckCircleFilled style={{ fontSize: 16, color: runwayColors.statusGreen }} />}
                </div>
                <div>
                    {effectiveTimezone ?
                        <TimeWithConditionalTz
                            triggeredAtLabel={displayTime}
                            triggeredByMoment={eventDate}
                            effectiveTimezone={effectiveTimezone}
                            use24HourTimeFormat={use24HourTimeFormat}
                            style={{ fontSize: 18, color: antGrey.grey8 }}
                        />
                    :   <Text style={{ fontSize: 18, color: antGrey.grey8 }}>{displayTime}</Text>}
                </div>
                <div>
                    <Text style={{ fontSize: '18px', color: antGrey.grey8, fontWeight: 600 }}>{displayDate}</Text>
                </div>
            </div>
        </div>
    );
};

export const DateBlock = ({
    step,
    releaseId,
    appId,
    releaseSummary,
}: {
    step: LifecycleStep;
    releaseId: string;
    appId: string;
    releaseSummary?: NextReleaseSummary | UpcomingReleaseSummary | CompletedReleaseSummary;
}) => {
    const {
        automationIcon,
        showCadenceIcon,
        isComplete,
        automationEnabled,
        targetDate,
        displayText,
        hasDate,
        displayTime,
        displayDate,
        displayTimeMoment,
    } = useReleaseLifecycleStepDisplayInfo(
        step,
        releaseId,
        appId,
        false,
        releaseSummary,
        'shortHourOnlyWithoutTimezone',
    );

    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, appId));
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, appId));

    return (
        <div style={{ minWidth: '180px' }}>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    paddingBottom: '6px',
                    gap: '10px',
                }}
            >
                {automationIcon}
                {showCadenceIcon && (
                    <RetweetOutlined
                        style={{
                            fontSize: 14,
                            color: antGrey.grey6,
                        }}
                    />
                )}
                <Text style={{ fontSize: '14px', color: antGrey.grey6 }}>
                    {!isComplete && (automationEnabled || targetDate.isValid()) ?
                        'Target ' + displayText + ' Date'
                    :   displayText}
                </Text>
                {isComplete && <CheckCircleFilled style={{ fontSize: 16, color: runwayColors.statusGreen }} />}
            </div>
            <div>
                {displayTimeMoment && effectiveTimezone ?
                    <TimeWithConditionalTz
                        triggeredAtLabel={displayTime}
                        triggeredByMoment={displayTimeMoment}
                        effectiveTimezone={effectiveTimezone}
                        use24HourTimeFormat={use24HourTimeFormat}
                        style={{ fontSize: 18, color: antGrey.grey8 }}
                    />
                :   <Text
                        style={{
                            fontSize: 18,
                            color: !hasDate ? antGrey.grey6 : antGrey.grey8,
                        }}
                    >
                        {displayTime}
                    </Text>
                }
            </div>
            <div>
                <Text
                    style={{
                        fontSize: '18px',
                        color: `${!hasDate ? antGrey.grey6 : antGrey.grey8}`,
                        fontWeight: 600,
                    }}
                >
                    {displayDate}
                </Text>
            </div>
        </div>
    );
};

export default TargetDatesSection;
